import Layout from '@/components/layout/Layout';
import Link from 'next/link';
import { FC } from 'react';
import { useRef, useState } from 'react';
import { parseISO, format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { getTruncatedTxt } from '@/helpers/helpers';
import BookInfoPodcast from '@/components/ui/book-info-podcast/BookInfoPodcast';
import BookBreadcrumbs from '@/components/ui/book-breadcrumbs/BookBreadcrumbs';
import BookTabs from '@/components/ui/book-tabs/BookTabs';
import ReviewsList from '@/components/ui/reviews-list/ReviewsList';
import QuoteCard from '@/components/ui/quote-card/QuoteCard';
import FormReview from '@/components/ui/form-review/FormReview';
import { getAuthorFullName, getShortTimeString } from '@/helpers/helpers';
import { useGetUrlPath } from '@/hooks/useGetUrlPath';
import { IPodcastsDetailData } from '@/interfaces/podcastsDetail.interface';
import { IReviews } from '@/interfaces/reviews.interface';
import { IQuotes, IQuotesData } from '@/interfaces/quotes.interface';
import { IBookPageForms } from '@/interfaces/bookPageForms';

import styles from './PodcastsDetail.module.scss';

interface IPodcastsDetailPage {
  book: IPodcastsDetailData;
  reviews: IReviews;
  quotes: IQuotes;
  isPodcastsPage?: boolean;
  isReviewsPage?: boolean;
  isQuotesPage?: boolean;
}

const PodcastsDetail: FC<IPodcastsDetailPage> = ({ book, reviews, quotes, isPodcastsPage, isReviewsPage, isQuotesPage }) => {
  const bookData = book;

  const [isFormsActive, setIsFormsActive] = useState<IBookPageForms>({ reviewFrom: false });
  const isPodcastsFree = book.allow_full_free === 1;

  const firstAuthorFullName = book.authors.length > 0 ? getAuthorFullName(book.authors[0].first_name, book.authors[0].middle_name, book.authors[0].last_name) : 'Неизвестен';

  const bookFullAlias = `/podcasts/${book.alias}/`;
  const refReviewForm = useRef<HTMLDivElement>(null);

  const SEO_TITLE = `Подкаст ${book.title} - слушать онлайн бесплатно или скачать в mp3 (мп3)`;
  const SEO_DESCRIPTION = `Подкаст ${book.title} ▶ слушать все выпуски аудиоподкаста онлайн бесплатно или скачать в mp3 (мп3) ▶ Электронная библиотека КучаКниг`;

  const SEO_CANONICAL = useGetUrlPath();
  const SEO_COVER = `https://kuchaknig.org/covers/${bookData.external_id}.jpg`;

  return (
    <Layout title={SEO_TITLE} description={SEO_DESCRIPTION} canonicalHref={SEO_CANONICAL} cover={SEO_COVER}>
      <BookBreadcrumbs bookTitle={book.title} bookAlias={book.alias} authorFullName={firstAuthorFullName} genres={[]} isPodcast createdAt={book.created_at} />
      <BookInfoPodcast book={book as any} formRef={refReviewForm} setIsFormsActive={setIsFormsActive} isPodcastsFree={isPodcastsFree} />
      <div className=""></div>
      <div className={styles.podcastsDescription}>{book.description}</div>
      <BookTabs
        bookPageName="Список выпусков"
        bookAlias={bookFullAlias}
        bookPage={isPodcastsPage}
        reviewsPage={isReviewsPage}
        quotesPage={isQuotesPage}
        reviews
        reviewsCount={reviews.total_count}
        quotes={quotes.data && quotes.data.length > 0}
        quotesCount={quotes.total_count}
      />
      {isPodcastsPage && (
        <ul className={styles.podcastsList}>
          {book.items &&
            book.items.reverse().map((item, index) => (
              <li key={item.id} className={styles.podcastsList__item}>
                <Link prefetch={false} href={`/podcasts/${book.alias}/${item.alias}/`} className={styles.podcastsList__title}>
                  {book.items.length - index}. {item.title}
                </Link>
                <div className={styles.podcastsList__info}>
                  <div className={styles.podcastsList__infoLeft}>
                    <span className={styles.podcastsList__date}>
                      <time dateTime={book.updated_at}>{format(parseISO(item.created_at), 'dd MMMM yyyy', { locale: ru })}</time>
                    </span>
                    <span className={styles.podcastsList__rating}>{item.rating}</span>
                  </div>
                  <div className={styles.podcastsList__infoRight}>
                    <span className={styles.podcastsList__time}>{getShortTimeString(item.duration)}</span>
                  </div>
                </div>
                <div className={styles.podcastsList__txt}>
                  {!!item.description && getTruncatedTxt(item.description, 200)}{' '}
                  {isPodcastsFree ? (
                    <Link prefetch={false} href={`/podcasts/${book.alias}/${item.alias}/`} className={styles.podcastsList__btnMore}>
                      Далее
                    </Link>
                  ) : (
                    <Link prefetch={false} target="_blank" href={`https://www.litres.ru/pages/biblio_book/?art=${item.external_id}`} className={styles.podcastsList__btnMore}>
                      Купить
                    </Link>
                  )}
                </div>
              </li>
            ))}
        </ul>
      )}

      {isPodcastsPage && (
        <>
          <div ref={refReviewForm}>
            <FormReview isReviewActive={isFormsActive.reviewFrom} bookId={book.id} bookAlias={book.alias} />
          </div>
        </>
      )}

      {isReviewsPage && (
        <>
          <div className={styles.titleWithButtons}>
            <div className={styles.titleWithButton__title}>Отзывы о книге «{book.title}»</div>
            <a href="#reviewForm" className={styles.titleWithButton__btn}>
              Добавить комментарий
            </a>
          </div>
          {!!reviews.data && <ReviewsList reviews={reviews} isReviewsPage={true} />}
          <div id="reviewForm">
            <FormReview bookId={book.id} bookAlias={book.alias} isReviewActive />
          </div>
        </>
      )}
      {isQuotesPage && (
        <>
          <div className={styles.titleWithButtons}>
            <div className={styles.titleWithButton__title}>Цитаты книги «{book.title}»</div>

          </div>

          <div className={styles.quotesWrap}>
            {quotes.data.map((quote: IQuotesData) => (
              <QuoteCard key={quote.id} quote={quote} />
            ))}
          </div>

        </>
      )}
    </Layout>
  );
};

export default PodcastsDetail;
